import React from "react";
import { motion } from "framer-motion";
import {
  Database,
  Radio,
  Code2,
  Bot,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const DASH_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89aa8e73-5a6c-41e7-aa52-5d2f193d3e77/images/cc9b8d2e72eddbeeb0b327e6879b70d1fe01bf9eeed24ffd8f4d0e1f69885f92.png";
const WAREHOUSE_IMG =
  "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc4OTM2MTA0fDA&ixlib=rb-4.1.0&q=85";

function ServiceCard({ icon: Icon, tag, title, desc, bullets, testid, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      data-testid={testid}
      className="group relative bg-white border border-slate-200 rounded-sm p-8 hover:border-[#1E5BD6]/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 h-full flex flex-col"
    >
      <div className="flex items-start justify-between">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-sm ${accent || "bg-blue-50 text-[#1E5BD6]"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-400 group-hover:text-[#1E5BD6] transition-colors">
          {tag}
        </span>
      </div>
      <h3 className="mt-6 font-heading text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
        {title}
      </h3>
      <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed flex-1">{desc}</p>
      <ul className="mt-6 space-y-2 border-t border-slate-100 pt-5">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
            <span className="h-1 w-1 rounded-full bg-[#1E5BD6]" />
            {b}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function Services() {
  const { t } = useLang();
  const s = t.services.items;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#1E5BD6]">
              <span className="h-px w-8 bg-[#1E5BD6]" />
              {t.services.label}
            </div>
            <h2
              data-testid="services-title"
              className="mt-5 font-heading font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            >
              {t.services.title}
            </h2>
          </div>
          <p className="lg:max-w-sm text-base text-slate-600 leading-relaxed">
            {t.services.subtitle}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {/* Featured ERP card spanning 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="service-erp-featured"
            className="md:col-span-2 relative overflow-hidden rounded-sm bg-[#0B1120] text-white p-8 lg:p-10 flex flex-col lg:flex-row gap-8 group"
          >
            <div className="flex-1 z-10">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-[#1E5BD6]">
                  <Database className="h-5 w-5 text-white" />
                </span>
                <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-400">
                  {s.erp.tag}
                </span>
              </div>
              <h3 className="mt-6 font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                {s.erp.title}
              </h3>
              <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed max-w-md">
                {s.erp.desc}
              </p>
              <ul className="mt-6 space-y-2">
                {s.erp.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="h-1 w-1 rounded-full bg-[#1E5BD6]" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 inline-flex items-center gap-2 text-xs font-semibold tracking-wider text-[#7BA7FF]">
                ODOO PARTNER WORKFLOW
                <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </div>
            <div className="relative lg:w-1/2 self-stretch rounded-sm overflow-hidden ring-1 ring-white/10 min-h-[200px]">
              <img
                src={DASH_IMG}
                alt="ERP dashboard"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1120]/60 via-transparent to-transparent" />
            </div>
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[radial-gradient(closest-side,rgba(30,91,214,0.5),transparent)]" />
          </motion.div>

          {/* RFID card with image */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="service-rfid"
            className="relative overflow-hidden rounded-sm bg-white border border-slate-200 hover:border-[#1E5BD6]/50 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={WAREHOUSE_IMG}
                alt="RFID warehouse"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-2.5 py-1 rounded-sm text-[10px] tracking-[0.24em] uppercase font-bold text-slate-700">
                <Radio className="h-3 w-3 text-[#1E5BD6]" />
                {s.rfid.tag}
              </span>
            </div>
            <div className="p-7 flex-1 flex flex-col">
              <h3 className="font-heading text-xl font-bold text-slate-900 tracking-tight">
                {s.rfid.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.rfid.desc}</p>
              <ul className="mt-4 space-y-1.5">
                {s.rfid.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-700">
                    <span className="h-1 w-1 rounded-full bg-[#1E5BD6]" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <ServiceCard
            testid="service-custom"
            icon={Code2}
            tag={s.custom.tag}
            title={s.custom.title}
            desc={s.custom.desc}
            bullets={s.custom.bullets}
          />
          <ServiceCard
            testid="service-automation"
            icon={Bot}
            tag={s.automation.tag}
            title={s.automation.title}
            desc={s.automation.desc}
            bullets={s.automation.bullets}
          />
          <ServiceCard
            testid="service-support"
            icon={ShieldCheck}
            tag={s.support.tag}
            title={s.support.title}
            desc={s.support.desc}
            bullets={s.support.bullets}
          />
        </div>
      </div>
    </section>
  );
}
